import {
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SelectProps,
  Typography,
  Box,
} from "@mui/material";
import { AnyFieldApi } from "@tanstack/react-form";
import { FC, forwardRef } from "react";

export interface SalifyFormSelectFieldProps {
  field: AnyFieldApi;
  selectFieldProps?: SelectFieldProps;
}

interface SelectFieldProps extends Omit<SelectProps, "value" | "onChange" | "onBlur" | "error"> {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
}

export interface VariantProps {
  options: Array<{ value: string; label: string }>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
}

const SalifyFormSelectFieldDefault = forwardRef<HTMLSelectElement, SalifyFormSelectFieldProps>(
  ({ field, selectFieldProps }, ref) => {
    const { options = [], placeholder, helperText, error, ...otherProps } = selectFieldProps || {};

    const hasError = field.state.meta.errors.length > 0 || error;
    const displayHelperText =
      field.state.meta.errors.length > 0 && field.state.meta.isTouched
        ? field.state.meta.errors.map((e) => e?.message).join(", ")
        : helperText;

    return (
      <Box width={selectFieldProps?.fullWidth ? "100%" : "auto"}>
        {selectFieldProps?.label && (
          <Typography
            component="label"
            variant="body2"
            sx={{
              display: "block",
              marginBottom: 1,
              color: hasError ? "error.main" : "text.primary",
              fontWeight: 500,
            }}
            htmlFor={field.name}
          >
            {selectFieldProps.label}
            {selectFieldProps?.required && (
              <span style={{ color: "#d32f2f", marginLeft: "4px" }}>*</span>
            )}
          </Typography>
        )}
        <FormControl
          variant="outlined"
          fullWidth={selectFieldProps?.fullWidth ?? true}
          error={hasError}
        >
          <Select
            {...otherProps}
            ref={ref}
            id={field.name}
            name={field.name}
            value={field.state.value ?? ""}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            displayEmpty={true}
            error={hasError}
            renderValue={(selected) => {
              if (!selected || selected === "") {
                return <span style={{ color: "#999" }}>{placeholder}</span>;
              }
              const selectedOption = options.find((option) => option.value === selected);
              return selectedOption ? selectedOption.label : selected;
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {displayHelperText && <FormHelperText>{displayHelperText}</FormHelperText>}
        </FormControl>
      </Box>
    );
  },
);

SalifyFormSelectFieldDefault.displayName = "SalifyFormSelectFieldDefault";

export default SalifyFormSelectFieldDefault;
